import { useEffect, useState } from 'react';
import { api } from '@/api/growth/dashboard/api';
import { Solar } from 'lunar-javascript';
import {
    FarmlandWeatherHourListItemVo,
    FarmlandWeatherListItem,
    GrowthDashboardReq,
} from '@/api/growth/dashboard/type';
import WeatherCard from '@/page/admin/growth/dashboard/WeatherCard';
import WeatherCardSmall from '@/page/admin/growth/dashboard/WeatherCardSmall';
import GrowthDashboardWeatherHourCard from './GrowthDashboardWeatherHourCard';

interface GrowthDashboardWeatherCardProps {
    gfIdx: number;
}

const GrowthDashboardWeatherCard = ({
    gfIdx,
}: GrowthDashboardWeatherCardProps) => {
    const [loading, setLoading] = useState(true);
    const [todayWeather, setTodayWeather] =
        useState<FarmlandWeatherListItem | null>(null);
    const [weeklyWeather, setWeeklyWeather] = useState<
        FarmlandWeatherListItem[]
    >([]);
    const [hourList, setHourList] = useState<FarmlandWeatherHourListItemVo[]>(
        [],
    );

    useEffect(() => {
        fetchWeatherList();
    }, [gfIdx]);

    const fetchWeatherList = async () => {
        setLoading(true);
        try {
            const req: GrowthDashboardReq = { gfIdx };
            const response = await api.getFarmlandWeatherList(req);

            console.log(response);
            if (response.data && response.data.list.length > 0) {
                setTodayWeather(response.data.list[0]);
                setWeeklyWeather(response.data.list.slice(1));
                setHourList(response.data.hourList || []);
            }
        } catch (error) {
            console.error('날씨 정보 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const convertToWeatherData = (item: FarmlandWeatherListItem) => ({
        weatherIdx: item.weatherIdx,
        date: item.wDate,
        dayOfWeek: item.dayOfWeek,
        weatherAm: item.weatherAm,
        weatherPm: item.weatherPm,
        rainRateAm: item.rainRateAm,
        rainRatePm: item.rainRatePm,
        minTemp: item.minTemp,
        maxTemp: item.maxTemp,
        rainfall: parseFloat(item.wthRain) || 0,
        humidity: item.humidity,
    });

    // 날짜 포맷 함수
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
    };

    // 주소 생성 함수
    const getFullAddress = (weather: FarmlandWeatherListItem) => {
        const parts = [
            weather.regionName1,
            weather.regionName2,
            weather.regionName3,
        ].filter(Boolean);
        return parts.join(' ');
    };

    // 강수확률 계산 (오전/오후 평균)
    const getAverageRainRate = (weather: FarmlandWeatherListItem) => {
        return Math.round((weather.rainRateAm + weather.rainRatePm) / 2);
    };

    if (loading) {
        return <div>날씨 정보 로딩중...</div>;
    }

    if (!todayWeather) {
        return <div>날씨 정보가 없습니다.</div>;
    }

    const getLunarDate = (dateStr: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const solar = Solar.fromYmd(
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate(),
        );
        const lunar = solar.getLunar();
        return `음력 ${lunar.getYear()}년 ${lunar.getMonth()}월 ${lunar.getDay()}일`;
    };

    return (
        <>
            <div className="row">
                <div className="col-12 col-md-4">
                    <WeatherCard weather={convertToWeatherData(todayWeather)} />
                </div>
                <div className="col-12 col-md-8">
                    <div className="card custom-card p-2 weather-card">
                        <div className="card-header">
                            <div className="d-flex justify-content-between w-100">
                                <div>
                                    <h4 className="mb-1">
                                        {formatDate(todayWeather.wDate)}
                                    </h4>
                                    <div className="lunar-date text-gray-7">
                                        {todayWeather.dayOfWeek} |{' '}
                                        {getLunarDate(todayWeather.wDate)}
                                    </div>
                                    <div className="location text-gray-7">
                                        <i className="bi bi-geo-alt-fill me-1"></i>
                                        {getFullAddress(todayWeather)}
                                    </div>
                                </div>
                                <div className="update-time">
                                    <span className="p-1 fs-11 bg-success text-fixed-white text-center rounded-1 lh-1 fw-medium">
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        업데이트: {todayWeather.regDt}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="card-body weather-info">
                            <div className="row weather-grid">
                                <div className="col-12 col-sm-3 weather-item weather-item-header">
                                    날씨
                                </div>
                                <div className="col-12 col-sm-3 weather-item">
                                    {todayWeather.weatherAm} /{' '}
                                    {todayWeather.weatherPm}
                                </div>
                                <div className="col-12 col-sm-3 weather-item weather-item-header">
                                    평균기온
                                </div>
                                <div className="col-12 col-sm-3 weather-item">
                                    {Math.round(
                                        (todayWeather.minTemp +
                                            todayWeather.maxTemp) /
                                            2,
                                    )}
                                    °C
                                </div>
                            </div>

                            <div className="row weather-grid">
                                <div className="col-12 col-sm-3 weather-item weather-item-header">
                                    최고기온
                                </div>
                                <div className="col-12 col-sm-3 weather-item">
                                    {todayWeather.maxTemp}°C
                                </div>
                                <div className="col-12 col-sm-3 weather-item weather-item-header">
                                    최저기온
                                </div>
                                <div className="col-12 col-sm-3 weather-item">
                                    {todayWeather.minTemp}°C
                                </div>
                            </div>

                            <div className="row weather-grid">
                                <div className="col-12 col-sm-3 weather-item weather-item-header">
                                    강수확률
                                </div>
                                <div className="col-12 col-sm-3 weather-item">
                                    {getAverageRainRate(todayWeather)}%
                                </div>
                                <div className="col-12 col-sm-3 weather-item weather-item-header">
                                    강수량
                                </div>
                                <div className="col-12 col-sm-3 weather-item">
                                    {todayWeather.wthRain}mm
                                </div>
                            </div>

                            <div className="row weather-grid">
                                <div className="col-12 col-sm-3 weather-item weather-item-header">
                                    일사량
                                </div>
                                <div className="col-12 col-sm-3 weather-item">
                                    {todayWeather.wthSrad} MJ/m²
                                </div>
                                <div className="col-12 col-sm-3 weather-item weather-item-header">
                                    습도
                                </div>
                                <div className="col-12 col-sm-3 weather-item">
                                    {todayWeather.humidity}%
                                </div>
                            </div>

                            <GrowthDashboardWeatherHourCard
                                hourList={hourList}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mt-3">
                <div className="col-12">
                    <div className="row">
                        {weeklyWeather.map(weather => (
                            <div
                                key={weather.weatherIdx}
                                className="col-12 col-md-4 col-xxl"
                            >
                                <WeatherCardSmall
                                    weather={convertToWeatherData(weather)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default GrowthDashboardWeatherCard;
